import React, { useState, useEffect } from 'react';
import { getAttendeeRoleLabel, formatMeetingDateTime, getMeetingDisplayName } from '../utils/meetingTemplates';
import api from '../../../services/api';
import Modal from '../../../components/Modal';

const ProposalResponsesModal = ({
  isOpen,
  onClose,
  meeting,
  onBook,
  onDelete,
  onReschedule
}) => {
  const [responses, setResponses] = useState(null);
  const [loading, setLoading] = useState(true);
  const [booking, setBooking] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Hämta svarsstatus
  useEffect(() => {
    if (!isOpen || !meeting?._id) return;

    const fetchResponses = async () => {
      try {
        setLoading(true);
        const response = await api.get(`/meetings/${meeting._id}/responses`);
        setResponses(response.data);
      } catch (err) {
        console.error('Fel vid hämtning av svarsstatus:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchResponses();
  }, [isOpen, meeting?._id]);

  const handleBook = async (proposalIndex) => {
    try {
      setBooking(true);
      await onBook(meeting._id, { selectedProposalIndex: proposalIndex });
      onClose();
    } catch (err) {
      console.error('Fel vid bokning:', err);
      alert(err.response?.data?.message || 'Kunde inte boka mötet');
    } finally {
      setBooking(false);
    }
  };

  /**
   * Boka en tid som inte är bland förslagen.
   *
   * Kunden kan ofta inte någon av tiderna men föreslår ingen ny i portalen —
   * man kommer överens på mail eller WhatsApp. Tidigare fick man då avboka
   * förslagen och skapa ett nytt möte från början. Backend bokar samma möte på
   * den nya tiden och tar bort förslagens PREL-möten (meetingController.bookMeeting).
   */
  const [egenDatum, setEgenDatum] = useState('');
  const [egenTid, setEgenTid] = useState('');
  const handleBookEgenTid = async () => {
    if (!egenDatum || !egenTid) return;
    try {
      setBooking(true);
      await onBook(meeting._id, { date: egenDatum, time: egenTid });
      onClose();
    } catch (err) {
      console.error('Fel vid bokning:', err);
      alert(err.response?.data?.message || 'Kunde inte boka mötet');
    } finally {
      setBooking(false);
    }
  };

  const handleDelete = async () => {
    try {
      setDeleting(true);
      await onDelete(meeting._id);
      onClose();
    } catch (err) {
      console.error('Fel vid borttagning:', err);
      alert(err.response?.data?.message || 'Kunde inte ta bort mötet');
    } finally {
      setDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  const handleReschedule = () => {
    onReschedule?.(meeting);
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="large">
          {/* Header */}
          <div className="bg-gray-50 px-6 py-4 border-b">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                Svarsstatus - {getMeetingDisplayName(meeting)}
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                Se vilka tider som passar deltagarna och välj slutgiltig tid
              </p>
            </div>
          </div>

          {/* Content */}
          <div className="px-6 py-4 max-h-[70vh] overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              </div>
            ) : responses?.proposals?.length > 0 ? (
              <div className="space-y-6">
                {responses.proposals.map((proposal, idx) => {
                  // Använd proposal.index (original index) för att matcha optimalProposalIndex
                  const isOptimal = responses.optimalProposalIndex === proposal.index;

                  return (
                    <div
                      key={idx}
                      className={`
                        border rounded-lg overflow-hidden
                        ${isOptimal ? 'border-green-300 ring-2 ring-green-100' : 'border-gray-200'}
                      `}
                    >
                      {/* Tid-header */}
                      <div className={`
                        px-4 py-3 flex items-center justify-between
                        ${isOptimal ? 'bg-green-50' : 'bg-gray-50'}
                      `}>
                        <div className="flex items-center gap-3">
                          <div>
                            <span className="font-medium text-gray-900">
                              {proposal.displayText || formatMeetingDateTime(proposal.date, proposal.time)}
                            </span>
                            {isOptimal && proposal.summary.allCanAttend && (
                              <span className="ml-2 px-2 py-0.5 bg-green-200 text-green-800 text-xs rounded-full">
                                Alla kan
                              </span>
                            )}
                            {isOptimal && !proposal.summary.allCanAttend && (
                              <span className="ml-2 px-2 py-0.5 bg-yellow-200 text-yellow-800 text-xs rounded-full">
                                Bästa alternativ
                              </span>
                            )}
                          </div>
                        </div>

                        <div className="flex items-center gap-4">
                          {/* Sammanställning */}
                          <div className="flex items-center gap-3 text-sm">
                            <span className="flex items-center text-green-600">
                              <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                              {proposal.summary.canAttend}
                            </span>
                            <span className="flex items-center text-red-600">
                              <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                              </svg>
                              {proposal.summary.cannotAttend}
                            </span>
                            {proposal.summary.pending > 0 && (
                              <span className="flex items-center text-yellow-600">
                                <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                                </svg>
                                {proposal.summary.pending}
                              </span>
                            )}
                          </div>

                          {/* Boka-knapp */}
                          <button
                            onClick={() => handleBook(proposal.index)}
                            disabled={booking}
                            className={`
                              px-4 py-1.5 rounded text-sm font-medium transition-colors
                              ${isOptimal
                                ? 'bg-green-600 text-white hover:bg-green-700'
                                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                              }
                              disabled:opacity-50
                            `}
                          >
                            {booking ? 'Bokar...' : 'Välj denna tid'}
                          </button>
                        </div>
                      </div>

                      {/* Deltagare */}
                      <div className="divide-y">
                        {proposal.responses.map((response, rIdx) => (
                          <div
                            key={rIdx}
                            className="px-4 py-2 flex items-center justify-between text-sm"
                          >
                            <div>
                              <span className="font-medium">{response.name}</span>
                              <span className="text-gray-500 ml-1">
                                ({getAttendeeRoleLabel(response.role)})
                              </span>
                            </div>
                            <div className="flex items-center">
                              {response.status === 'sender' && (
                                <span className="text-gray-400 text-xs">Avsändare</span>
                              )}
                              {response.status === 'can_attend' && (
                                <span className="flex items-center text-green-600">
                                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                  </svg>
                                  <span className="ml-1">Kan</span>
                                </span>
                              )}
                              {response.status === 'cannot_attend' && (
                                <span className="flex items-center text-red-600">
                                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                  </svg>
                                  <span className="ml-1">Kan inte</span>
                                </span>
                              )}
                              {response.status === 'pending' && (
                                <span className="flex items-center text-yellow-600">
                                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                                  </svg>
                                  <span className="ml-1">Väntar</span>
                                </span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-12 text-gray-500">
                Inga tidsförslag att visa
              </div>
            )}

            {/* Egen tid — när man kommit överens utanför portalen */}
            {!loading && (
              <div className="mt-6 border border-dashed border-gray-300 rounded-lg p-4">
                <h3 className="text-sm font-medium text-gray-900">Boka en annan tid</h3>
                <p className="text-xs text-gray-500 mt-0.5">
                  Har ni bestämt en tid via mail eller telefon? Boka den direkt — tidsförslagen
                  och deras preliminära kalenderbokningar tas bort och kallelsen skickas som vanligt.
                </p>
                <div className="mt-3 flex flex-wrap items-end gap-3">
                  <label className="text-xs text-gray-600">
                    Datum
                    <input
                      type="date"
                      value={egenDatum}
                      min={new Date().toLocaleDateString('sv-SE')}
                      onChange={(e) => setEgenDatum(e.target.value)}
                      className="mt-1 block px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                    />
                  </label>
                  <label className="text-xs text-gray-600">
                    Tid
                    <input
                      type="time"
                      step="900"
                      value={egenTid}
                      onChange={(e) => setEgenTid(e.target.value)}
                      className="mt-1 block px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                    />
                  </label>
                  <button
                    onClick={handleBookEgenTid}
                    disabled={booking || !egenDatum || !egenTid}
                    className="px-4 py-1.5 rounded text-sm font-medium bg-green-600 text-white hover:bg-green-700 disabled:opacity-50"
                  >
                    {booking ? 'Bokar...' : 'Boka tiden'}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Delete confirmation dialog */}
          {showDeleteConfirm && (
            <div className="absolute inset-0 bg-white bg-opacity-95 flex items-center justify-center z-10 rounded-lg">
              <div className="text-center p-6 max-w-sm">
                <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
                  <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Ta bort möte?</h3>
                <p className="text-sm text-gray-600 mb-6">
                  Detta tar bort mötet och alla tidsförslag. Eventuella PREL-möten i kalendern kommer också att tas bort.
                </p>
                <div className="flex gap-3 justify-center">
                  <button
                    onClick={() => setShowDeleteConfirm(false)}
                    disabled={deleting}
                    className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                  >
                    Avbryt
                  </button>
                  <button
                    onClick={handleDelete}
                    disabled={deleting}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
                  >
                    {deleting ? 'Tar bort...' : 'Ta bort'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Footer */}
          <div className="bg-gray-50 px-6 py-4 border-t flex justify-between">
            <div className="flex gap-2">
              <button
                onClick={() => setShowDeleteConfirm(true)}
                disabled={booking || deleting}
                className="px-4 py-2 text-red-600 bg-white border border-red-200 rounded-lg hover:bg-red-50 disabled:opacity-50"
              >
                Ta bort möte
              </button>
              <button
                onClick={handleReschedule}
                disabled={booking || deleting}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
              >
                Föreslå nya tider
              </button>
            </div>
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Stäng
            </button>
          </div>
    </Modal>
  );
};

export default ProposalResponsesModal;
